import React from "react";
import { Phone, Mail, MapPin, User } from "lucide-react";
import { resolveImageUrl } from "../quote-templates/ContentBlocks";

export function CoverPageDisplay({
  headline = "PROPOSAL",
  subheadline = "",
  logoUrl = "",
  bgImageUrl = "",
  bgColor = "#0F4C81",
  companyName = "",
  companyPhone = "",
  companyEmail = "",
  companyAddress = "",
  contactName = "",
  projectAddress = "",
  quoteNumber = "",
  date = "",
  showLogo = true,
  showDate = true,
  showQuoteNumber = true,
  showContactInfo = true,
  showCompanyInfo = true,
  compact = false,
}) {
  const displayDate = date || new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const resolvedBgImage = resolveImageUrl(bgImageUrl);
  const resolvedLogo = resolveImageUrl(logoUrl);

  if (compact) {
    return (
      <div
        className="relative overflow-hidden rounded-lg"
        style={{ backgroundColor: bgColor, minHeight: 180 }}
        data-testid="cover-preview-compact"
      >
        <div className="flex flex-col items-center justify-center text-center p-4" style={{ minHeight: 180 }}>
          {showLogo && resolvedLogo && (
            <img src={resolvedLogo} alt="Logo" className="h-10 object-contain mb-2 rounded" />
          )}
          {showLogo && !resolvedLogo && companyName && (
            <p className="text-white/60 text-xs font-medium tracking-wider uppercase mb-2">{companyName}</p>
          )}
          <h2 className="text-white text-lg font-bold">{headline || "PROPOSAL"}</h2>
          {showDate && <p className="text-white/60 text-xs mt-1">{displayDate}</p>}
          {contactName && <p className="text-white/80 text-xs mt-1">{contactName}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl shadow-xl" data-testid="cover-page-display">
      <div
        className="flex flex-col items-center justify-center text-center px-8 py-16"
        style={{
          backgroundColor: bgColor,
          backgroundImage: resolvedBgImage ? `linear-gradient(rgba(15,76,129,0.85),rgba(15,76,129,0.85)), url(${resolvedBgImage})` : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
          minHeight: 420,
        }}
      >
        {/* Logo */}
        {showLogo && resolvedLogo && (
          <img
            src={resolvedLogo}
            alt="Company Logo"
            className="max-h-28 w-auto object-contain mb-6 rounded-xl"
            data-testid="cover-logo"
          />
        )}

        {/* Text fallback when no logo */}
        {showLogo && !resolvedLogo && companyName && (
          <div className="mb-6">
            <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center mx-auto mb-2 border border-white/20">
              <span className="text-2xl font-black text-white">{companyName.charAt(0)}</span>
            </div>
            <p className="text-white/70 text-sm font-medium tracking-wider uppercase">{companyName}</p>
          </div>
        )}

        {/* Headline */}
        <h1
          className="text-white font-bold leading-tight"
          style={{ fontSize: "clamp(1.5rem, 4vw, 2.5rem)" }}
          data-testid="cover-headline-display"
        >
          {headline || "PROPOSAL"}
        </h1>

        {subheadline && (
          <p className="text-white/70 text-base mt-2">{subheadline}</p>
        )}

        <div className="w-16 h-0.5 bg-white/30 mx-auto my-5" />

        {showDate && <p className="text-white/70 text-sm">{displayDate}</p>}
        {showQuoteNumber && quoteNumber && <p className="text-white/50 text-xs mt-1 tracking-wider">{quoteNumber}</p>}
        {showContactInfo && contactName && <p className="text-white font-medium text-lg mt-4">{contactName}</p>}
        {showContactInfo && projectAddress && <p className="text-white/60 text-sm mt-1">{projectAddress}</p>}
      </div>

      {/* Bottom contact bar - Company info only */}
      {showCompanyInfo && (companyName || companyPhone || companyEmail || companyAddress) && (
        <div className="bg-slate-900 text-white px-6 py-4" data-testid="cover-contact-bar">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
            {companyName && (
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <User className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <span>{companyName}</span>
              </div>
            )}
            {companyPhone && (
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <Phone className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <span>{companyPhone}</span>
              </div>
            )}
            {companyEmail && (
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <Mail className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <span>{companyEmail}</span>
              </div>
            )}
            {companyAddress && (
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <span>{companyAddress}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
